import { ArrowDownTrayIcon, DocumentArrowUpIcon } from '@heroicons/react/24/solid';
import Papa from 'papaparse/papaparse.min.js';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import type { Company } from '../../../@types/Company';
import type { RootState } from '../../../store/store';
import { JWTAuthorization } from '../../../utils/JWTHeader';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import CompanySearch from '../components/CompanySearch';

const validateSiret = (siret: string): boolean => {
  // Check if the SIRET is exactly 14 digits
  if (!/^\d{14}$/.test(siret)) {
    return false;
  }

  // Luhn algorithm check
  let sum = 0;
  let isEven = false;

  for (let i = siret.length - 1; i >= 0; i--) {
    let digit = parseInt(siret.charAt(i), 10);

    if (isEven) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    isEven = !isEven;
  }

  return sum % 10 === 0;
};

const SiretTrackedImport: React.FC = () => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
  };

  const parseCSV = (csvFile: File) => {
    Papa.parse(csvFile, {
      complete: (results) => {
        const validSirets = (results.data as string[])
          .flat()
          .filter((siret) => validateSiret(siret.trim()));
        setPreviewData(validSirets.slice(0, 5));
      },
      error: (error) => {
        console.error('Error parsing CSV :', error);
        showToast('error', 'Erreur lors de la lecture du fichier CSV.');
      },
    });
  };

  const handleFile = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    if (selectedFile.name.endsWith('.csv')) {
      parseCSV(selectedFile);
    } else {
      showToast('error', 'Format de fichier non supporté. Veuillez utiliser un fichier CSV.');
    }
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      handleFile(selectedFile);
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleImport = async() => {
    if (!file) {
      showToast('error', 'Veuillez sélectionner un fichier à importer.');
      return;
    }

    if (!selectedCompany) {
      showToast('error', 'Veuillez sélectionner une entreprise.');
      return;
    }

    setImporting(true);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`/api/companies/${selectedCompany.id}/sirets-tracked/upload`, {
        method: 'POST',
        headers: {
          Authorization: JWTAuthorization(jwt),
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Import failed');
      }

      const result = await response.json();
      showToast('success', 'Import réussi.');

      // Reset fields
      setFile(null);
      setPreviewData([]);

      // Show success message
      setImportResult({
        success: true,
        message: `${result.importedCount} SIRET(s) importé(s) avec succès.`,
      });
    } catch (error) {
      console.error('Import error :', error);
      showToast('error', 'Une erreur est survenue lors de l\'import.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="mx-auto">
      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
          <ArrowDownTrayIcon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
          Import de SIRET à surveiller
        </h1>
        <p className="text-gray-600">Importez facilement une liste de SIRET à surveiller pour une entreprise.</p>
      </div>

      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">Sélectionner une entreprise</label>
          <CompanySearch onCompanySelect={handleCompanySelect} />
        </div>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-700">Sélectionner un fichier CSV</label>
          <div
            className={`flex w-full items-center justify-center ${dragActive ? 'border-blue-500' : 'border-gray-300'}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <label htmlFor="dropzone-file" className="flex h-64 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <DocumentArrowUpIcon className="mb-3 h-10 w-10 text-gray-400" />
                <p className="mb-2 text-sm text-gray-500">
                  <span className="font-semibold">Cliquez pour télécharger</span>
                  {' '}
                  ou glissez et déposez
                </p>
                <p className="text-xs text-gray-500">CSV (MAX. 5Mo)</p>
              </div>
              <input
                id="dropzone-file"
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept=".csv"
              />
            </label>
          </div>
          {file && (
            <p className="mt-2 text-sm text-gray-600">
              Fichier sélectionné :
              {' '}
              {file.name}
            </p>
          )}
        </div>

        {previewData.length > 0 && (
          <div className="mb-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-800">Aperçu des SIRET valides (5 premiers)</h2>
            <ul className="list-disc pl-5">
              {previewData.map((siret, index) => (
                <li key={index} className="text-gray-600">{siret}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex items-center justify-between">
          <button
            onClick={handleImport}
            disabled={importing || !selectedCompany || !file}
            className="cursor-pointer rounded bg-rgpd-green px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {importing ? (
              <>
                <Spinner size="sm" />
                <span className="ml-2">Import en cours...</span>
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="mr-2 inline h-5 w-5 shrink-0" />
                Importer
              </>
            )}
          </button>

          {importing === false && importResult === null && (!selectedCompany || !file) && (
            <Badge
              mode="warning"
              text="Sélectionnez une entreprise et un fichier pour importer la liste des SIRET à surveiller"
            />
          )}
        </div>

        {importResult && importResult.success && (
          <div className="mt-6 flex flex-col items-center">
            <Badge
              mode="success"
              text={importResult.message}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SiretTrackedImport;
